import React, { useState, useEffect } from "react";
import { X, Gift, ArrowRight, Copy, Check } from "lucide-react";
import { getPopupSettings } from "../lib/api";

export default function PromoPopup() {
    const [config, setConfig] = useState(null);
    const [visible, setVisible] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // Don't show on admin pages
        if (window.location.pathname.startsWith("/sprava")) return;

        const dismissed = localStorage.getItem("sg_popup_dismissed");
        if (dismissed === "true") return;

        getPopupSettings().then(r => {
            const cfg = r.data;
            if (!cfg.enabled) return;
            setConfig(cfg);
            const delay = (cfg.delay_seconds || 10) * 1000;
            const timer = setTimeout(() => setVisible(true), delay);
            return () => clearTimeout(timer);
        }).catch(() => {});
    }, []);

    const handleClose = () => {
        setVisible(false);
        if (config?.show_once) {
            localStorage.setItem("sg_popup_dismissed", "true");
        }
    };

    const handleCopy = () => {
        if (config?.discount_code) {
            navigator.clipboard.writeText(config.discount_code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (!visible || !config) return null;

    return (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4" data-testid="promo-popup-overlay">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300" data-testid="promo-popup">
                {/* Close */}
                <button onClick={handleClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 z-10 p-1 rounded-full hover:bg-gray-100" data-testid="promo-popup-close">
                    <X size={20} />
                </button>

                {/* Header */}
                <div className="bg-gradient-to-br from-green-600 to-green-700 px-6 py-8 text-center text-white">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Gift size={30} className="text-yellow-300" />
                    </div>
                    <h2 className="text-2xl font-bold mb-1">{config.title}</h2>
                    <p className="text-green-100 text-sm">{config.description}</p>
                </div>

                {/* Code */}
                <div className="px-6 py-6">
                    {config.discount_code && (
                        <div className="mb-4">
                            <p className="text-xs font-medium text-gray-500 mb-2 text-center">Váš slevový kód:</p>
                            <div className="flex items-center justify-center gap-2">
                                <div className="bg-gray-100 border-2 border-dashed border-green-400 rounded-lg px-5 py-2.5 font-mono font-bold text-lg text-green-700 tracking-wider">
                                    {config.discount_code}
                                </div>
                                <button onClick={handleCopy} className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors" data-testid="promo-popup-copy">
                                    {copied ? <Check size={18} /> : <Copy size={18} />}
                                </button>
                            </div>
                        </div>
                    )}
                    {config.discount_percent > 0 && (
                        <p className="text-center text-gray-600 text-sm mb-4">
                            Sleva <strong className="text-green-600">{config.discount_percent}%</strong> na první objednávku
                        </p>
                    )}
                    <div className="flex gap-2">
                        {config.cta_link && (
                            <a href={config.cta_link}
                                className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white font-semibold py-2.5 px-4 rounded-xl hover:bg-green-700 transition-colors"
                                data-testid="promo-popup-cta"
                                onClick={handleClose}>
                                {config.cta_text || "Získat slevu"} <ArrowRight size={16} />
                            </a>
                        )}
                        <button onClick={handleClose} className="px-4 py-2.5 text-gray-500 text-sm hover:text-gray-700 transition-colors">
                            Ne, děkuji
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
